/**
 * Browser-side scan helpers (Ops). The platform-neutral decisions - what is a scan, how it is normalised, when
 * a repeat is a duplicate - live in `@wms/scan-input` and are shared with the Android app (ADR-091). What stays
 * here is only what needs the DOM: turning a `KeyboardEvent` into a character (layout-independent) and telling
 * whether the focused element is somewhere a person types.
 */
import {
  DEDUPE_DEFAULTS, KEYBOARD_DEFAULTS_BROWSER, normalizeScanValue,
  type KeyboardConfig,
} from '@wms/scan-input';

/** Keyboard timing (shared profile) plus the same-code window used for the "same code again" notice. */
export type ScanConfig = KeyboardConfig & {
  /** The same code again within this window is a repeated trigger pull, shown as a notice and not counted. */
  readonly duplicateWindowMs: number;
};

export const DEFAULT_SCAN_CONFIG: ScanConfig = {
  ...KEYBOARD_DEFAULTS_BROWSER,
  duplicateWindowMs: DEDUPE_DEFAULTS.sameProviderMs,
};

export type KeyLike = {
  readonly key: string;
  readonly code: string;
  readonly shiftKey?: boolean;
  readonly ctrlKey?: boolean;
  readonly altKey?: boolean;
  readonly metaKey?: boolean;
};

// Shifted symbols of the US layout for the physical keys a Code 128 label may use.
const SHIFTED_DIGIT = [')', '!', '@', '#', '$', '%', '^', '&', '*', '('];
const PUNCTUATION: Record<string, readonly [string, string]> = {
  Minus: ['-', '_'],
  Equal: ['=', '+'],
  Period: ['.', '>'],
  Comma: [',', '<'],
  Slash: ['/', '?'],
  Semicolon: [';', ':'],
  Backslash: ['\\', '|'],
  Quote: ["'", '"'],
  BracketLeft: ['[', '{'],
  BracketRight: [']', '}'],
  Backquote: ['`', '~'],
  Space: [' ', ' '],
};

/**
 * The character a keystroke stands for, or null for keys that carry none (Shift, arrows, F-keys...).
 * `event.key` is trusted when it is plain ASCII; when it is not (a scanner's digits and letters arriving
 * through a Russian layout turn into Cyrillic), the physical key `event.code` is used to rebuild the Latin
 * character - the barcode alphabet is Latin + digits, and the scanner sends physical key presses.
 */
export function keyToChar(e: KeyLike): string | null {
  if (e.ctrlKey || e.altKey || e.metaKey) return null;
  const { key, code } = e;
  if (key.length === 1) {
    const cp = key.charCodeAt(0);
    if (cp >= 0x20 && cp < 0x7f) return key;
  } else if (key !== 'Unidentified' && key !== 'Dead' && key !== 'Process') {
    return null; // Shift, Enter, ArrowLeft...
  }
  const shift = e.shiftKey === true;
  const letter = /^Key([A-Z])$/.exec(code);
  if (letter) return shift ? letter[1]! : letter[1]!.toLowerCase();
  const digit = /^(?:Digit|Numpad)([0-9])$/.exec(code);
  if (digit) return shift && code.startsWith('Digit') ? SHIFTED_DIGIT[Number(digit[1])]! : digit[1]!;
  const punct = PUNCTUATION[code];
  if (punct) return shift ? punct[1] : punct[0];
  if (code === 'NumpadDecimal') return '.';
  if (code === 'NumpadSubtract') return '-';
  if (code === 'NumpadDivide') return '/';
  return null;
}

/**
 * Surrounding blanks, control characters and an AIM symbology identifier go; the payload is never altered
 * (no case change - labels are case sensitive). '' when nothing usable is left.
 */
export function normalizeScanCode(raw: string): string {
  const r = normalizeScanValue(raw);
  return r.ok ? r.value : '';
}

/** True for elements a person types into (the global capture must never take their keystrokes). */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || typeof (el as HTMLElement).tagName !== 'string') return false;
  const node = el as HTMLElement;
  const tag = node.tagName.toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') {
    const type = ((node as HTMLInputElement).type || 'text').toLowerCase();
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'image', 'file', 'range', 'color'].includes(type);
  }
  return node.isContentEditable === true;
}
